import { Link, useOutletContext } from '@remix-run/react';
import { BadgePercent, Loader2Icon, Plus } from 'lucide-react';
import { useState } from 'react';
import { SearchQuery } from '~/generated/graphql';
import { OutletContext } from '~/types';
import { calculateEffectivePrice } from '~/utils/pricing';
import { Price } from './Price';

export type ProductCardProps = SearchQuery['search']['items'][number];

export function ProductCard({
  productAsset,
  productName,
  slug,
  priceWithTax,
  currencyCode,
  description,
  productVariantId,
  productVariant,
  inStock,
  supplier,
}: ProductCardProps & { supplier?: { name: string; status: string } | null }) {
  const { activeOrderFetcher: fetcher, merchantContextRequired } =
    useOutletContext<OutletContext>();
  const [quantity, setQuantity] = useState<number>(1);

  // Check if item is in cart
  const { activeOrder } = fetcher.data ?? {};
  const isItemInCart =
    activeOrder?.lines.some(
      (line) => line.productVariant.id === productVariantId,
    ) ?? false;

  const basePrice =
    typeof priceWithTax === 'object' && 'value' in priceWithTax
      ? priceWithTax.value
      : typeof priceWithTax === 'object' && 'min' in priceWithTax
      ? priceWithTax.min
      : 0;

  const priceTiers = productVariant?.customFields?.priceTiers || [];
  const hasPriceTiers = Array.isArray(priceTiers) && priceTiers.length > 0;

  const { effectiveUnitPrice } = calculateEffectivePrice(
    basePrice || 0,
    priceTiers,
    quantity,
  );

  const price = effectiveUnitPrice;

  const customOriginalPrice = productVariant?.customFields?.originalPrice;
  const originalPrice =
    customOriginalPrice && customOriginalPrice > price
      ? customOriginalPrice
      : basePrice && basePrice > price
      ? basePrice
      : undefined;

  const isBeingAddedToCart =
    fetcher.state === 'submitting' &&
    fetcher.formData?.get('variantId') === productVariantId;

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!fetcher || !productVariantId || !inStock || merchantContextRequired)
      return;

    fetcher.submit(
      {
        action: 'addItemToOrder',
        variantId: productVariantId,
        quantity: quantity.toString(),
      },
      {
        method: 'post',
        action: '/api/active-order',
      },
    );
  };

  return (
    <div className="flex flex-col h-full bg-transparent">
      {/* Image Container with + Button */}
      <div className="relative">
        <Link
          to={`/products/${slug}`}
          prefetch="intent"
          className="relative block w-full aspect-square overflow-hidden rounded-2xl"
        >
          {(originalPrice ?? 0) > price && (
            <div className="absolute top-0 left-0 bg-[#E80245] text-white text-xs font-bold px-3 py-1 font-serif z-10">
              Sale
            </div>
          )}
          {productAsset?.preview ? (
            <img
              src={`${productAsset.preview}?w=300&h=300&format=webp`}
              alt={productName}
              className={`w-full h-full object-contain transition-transform duration-300 hover:scale-105 ${
                !inStock ? 'grayscale opacity-70' : ''
              }`}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <img
                src="/brand_logo.png"
                alt="Logo thumbnail"
                className="w-1/2 h-auto opacity-50"
                loading="lazy"
              />
            </div>
          )}
        </Link>

        {/* Floating + Button - Bottom Right */}
        {inStock && (
          <div
            className="absolute bottom-3 right-3 z-10"
            onClick={stopPropagation}
          >
            <button
              type="button"
              onClick={handleAddToCart}
              className="w-9 h-9 rounded-full bg-white border border-gray-100 shadow-lg flex items-center justify-center text-gray-600 hover:text-primary hover:border-primary/30 active:scale-95 transition-all duration-200 disabled:opacity-50"
              disabled={isBeingAddedToCart || merchantContextRequired}
              aria-label="Add to cart"
            >
              {isBeingAddedToCart ? (
                <Loader2Icon size={18} className="animate-spin" />
              ) : (
                <Plus size={20} strokeWidth={2} />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="flex flex-col flex-1 pt-3 px-1">
        {/* Price */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-gray-900 font-bold text-lg tracking-tight">
            <Price priceWithTax={price} currencyCode={currencyCode} />
          </span>
          {(originalPrice ?? 0) > price && (
            <div className="relative inline-block">
              <span className="text-gray-400 text-xs">
                <Price
                  priceWithTax={originalPrice}
                  currencyCode={currencyCode}
                />
              </span>
              <div className="absolute top-1/2 left-0 w-full h-[1px] bg-[#E80245] -translate-y-1/2 transform rotate-[-3deg]"></div>
            </div>
          )}
        </div>

        {/* Title */}
        <Link
          to={`/products/${slug}`}
          prefetch="intent"
          className="no-underline"
        >
          <h3 className="line-clamp-2 text-sm font-normal leading-snug text-gray-800 hover:text-primary transition-colors">
            {productName}
          </h3>
        </Link>

        {/* Description */}
        <p className="text-xs text-gray-400 mt-0.5 mb-2">
          {description
            ? description.replace(/<[^>]*>/g, '').slice(0, 30)
            : '400ml • 1 pcs'}
        </p>
        {supplier?.name && (
          <p className="text-xs font-medium text-gray-500 mb-2">
            Sold by {supplier.name}
          </p>
        )}

        {/* Bulk Pricing Badges */}
        {hasPriceTiers && (
          <div className="flex flex-col gap-0.5 mt-auto">
            {priceTiers.slice(0, 2).map((tier, idx) => (
              <div
                key={idx}
                className="flex items-center gap-1 text-[#E80245] text-xs font-medium"
              >
                <BadgePercent size={12} className="flex-shrink-0" />
                <span>
                  <Price
                    priceWithTax={(tier.price ?? 0) * 100}
                    currencyCode={currencyCode}
                    variant="primary"
                  />
                  /pack for {tier.minQty} packs+
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
